import React, { useState, useEffect, useContext } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useNavigate } from 'react-router-dom';

import {
  Text,
  Table,
  Box,
  Flex,
  useColorModeValue,
  Thead,
  Tr,
  Td,
  Th,
  Tbody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
} from '@chakra-ui/react';

import Paginate from 'components/paginate/Paginate';
import { SortableTh } from 'components/table';
import type { SortState } from 'components/table';

import { apiAativeLeadList } from 'api';

import { useViewAs } from 'hooks/useViewAs';

import { LEAD_STATUS_LOST } from 'views/leads/constants';

import type {
  APIPaginatedResponse,
  APILeadsListResultData,
  APIStatusChangeData,
} from 'types/api.types';

import LeadStatusChange from 'views/leads/components/LeadStatusChange/LeadStatusChange';
import LeadArchive from 'views/leads/components/LeadArchive/LeadArchive';
import LeadFileUpload from 'views/leads/components/LeadFileUpload/LeadFileUpload';
import LeadAddNote from 'views/leads/components/LeadAddNote/LeadAddNote';
import LeadHot from 'views/leads/components/LeadHot';

import getDateAge from 'helpers/getDateAge';

import {
  ROLE_ACCOUNT_MANAGER,
  ROLE_ACCOUNT_ASSISTANT,
} from 'variables/constants';

import { AuthContext } from 'contexts/AuthContext';

const TABLE_HEADERS: { label: string; field?: string }[] = [
  { label: 'Hot', field: 'hot' },
  { label: 'Client name', field: 'client_name' },
  { label: 'Type', field: 'type' },
  { label: 'Workflow', field: 'workflow' },
  { label: 'Source', field: 'source' },
  { label: 'Premium', field: 'premium' },
  { label: 'Age', field: 'age' },
  { label: 'Status' },
  { label: 'Actions' },
  { label: 'Delete' },
];

const ActiveLeadsList = ({
  searchQuery,
  sortQuery,
  sort,
  setLeadCount,
}: {
  searchQuery: string;
  sortQuery: string;
  sort: SortState;
  setLeadCount: (count: number) => void;
}) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const viewAs = useViewAs();

  const [page, setPage] = useState(0);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inactiveRowBgColor = useColorModeValue('yellow.50', 'yellow.900');
  const hoverBgColor = useColorModeValue('gray.200', 'gray.600');

  const query = [searchQuery, sortQuery].filter(Boolean).join('&');

  const { error, data, isLoading, refetch } = useQuery({
    queryKey: ['active-lead-list', page, query, viewAs.filterParams],
    queryFn: () =>
      apiAativeLeadList(
        page + 1,
        query,
        viewAs.viewAsEmail,
        viewAs.viewAsBranchId,
      ),
    refetchInterval: 120000, // 2 minutes
  }) as {
    data: APIPaginatedResponse<APILeadsListResultData>;
    isLoading: boolean;
    error: any;
    refetch: () => void;
    isFetching: boolean;
  };

  useEffect(() => {
    if (searchQuery) {
      setPage(0);
    }
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [sortQuery]);

  useEffect(() => {
    if (!data) return;
    setLeadCount(data.count);
  }, [data, setLeadCount]);

  const onLeadClick = (lead: APILeadsListResultData) => {
    navigate(`/admin/leads/${lead.id}`);
  };

  const onStatusChangeSuccess = (data: APIStatusChangeData) => {
    if (data.status === LEAD_STATUS_LOST) {
      refetch();
    }
  };

  if (isLoading || !data?.results) {
    return (
      <Box textAlign="center" mt="20px">
        Loading active leads...
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="20px" pb="0">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{JSON.stringify(error)}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Table variant="simple" color="gray.500" mt="20px">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map(({ label, field }) => {
              const thProps = {
                borderColor,
                borderTop: '1px solid',
                borderTopColor: borderColor,
              };

              return field ? (
                <SortableTh
                  key={label}
                  label={label}
                  field={field}
                  {...sort}
                  {...thProps}
                />
              ) : (
                <Th key={label} pe="10px" color="gray.400" {...thProps}>
                  {label}
                </Th>
              );
            })}
          </Tr>
        </Thead>

        <Tbody>
          {data.results.map((lead) => (
            <Tooltip
              key={lead.id}
              label="This lead hasn't been actioned in over 5 days"
              isDisabled={!lead.is_inactive}
              placement="top"
              hasArrow
            >
              <Tr
                key={lead.id}
                onClick={() => onLeadClick(lead)}
                cursor="pointer"
                bg={lead.is_inactive ? inactiveRowBgColor : 'inherit'}
                _hover={{
                  bg: hoverBgColor,
                }}
              >
                <Td>
                  <Flex align="center" justify="center">
                    <LeadHot lead={lead} />
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.first_name} {lead.last_name}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.type_display}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.workflow?.name}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.source?.name}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.value}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text
                      color={textColor}
                      fontSize="sm"
                      fontWeight="700"
                      whiteSpace="nowrap"
                    >
                      {getDateAge(new Date(lead.created_at))}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  {![ROLE_ACCOUNT_MANAGER, ROLE_ACCOUNT_ASSISTANT].includes(
                    user.role,
                  ) ? (
                    <Flex align="center">
                      <LeadStatusChange
                        lead={lead}
                        onSuccess={onStatusChangeSuccess}
                        showProcessedBy
                      />
                    </Flex>
                  ) : (
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.status_display}
                    </Text>
                  )}
                </Td>
                <Td>
                  <Flex gap={4}>
                    <LeadFileUpload lead={lead} /> <LeadAddNote lead={lead} />
                  </Flex>
                </Td>
                <Td>
                  <Box textAlign={'center'}>
                    <LeadArchive lead={lead} onSuccess={refetch} />
                  </Box>
                </Td>
              </Tr>
            </Tooltip>
          ))}
        </Tbody>
      </Table>

      {data.results.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No leads found
        </Box>
      )}

      <Paginate page={page} count={data.count} onSetPage={setPage} />
    </>
  );
};

export default React.memo(ActiveLeadsList);
